// Function to get vehicle model options for a given vehicle mark
import { vehicleMarksAndModels } from "./codelists";

// Define types for vehicle marks and models
export interface VehicleOption {
  key: string;
  text: string;
}

// Function to get models for a specific vehicle mark
export const getModelsByMark = (mark: string): VehicleOption[] => {
  if (!mark) {
    // If no valid mark is provided, return an empty list
    return [{ key: "", text: "" }];
  }

  // Check if the mark exists in our vehicleMarksAndModels
  const normalizedMark = mark.toUpperCase().trim();
  let allModels: string[] = [];

  // Handle the issue with entries like "VOLKSWAGEN" cena="bezna":
  // We need to search for the mark as a prefix in the object keys and merge all models
  for (const markKey in vehicleMarksAndModels) {
    // Check if the key starts with our mark (handling the issue with malformed keys)
    const cleanMarkKey = (markKey.split('"')[0] ?? "").trim().toUpperCase();
    if (cleanMarkKey === normalizedMark) {
      const models = vehicleMarksAndModels[markKey];
      if (models && Array.isArray(models)) {
        // Merge models from all matching entries
        allModels = [...allModels, ...models];
      }
    }
  }

  if (!allModels || allModels.length === 0) {
    console.log(`[ModelFilter] No models found for mark: ${mark}`);
    return [{ key: "", text: "" }];
  } // Remove duplicates and filter out empty models
  const filteredModels = allModels.filter((model) => model && model.trim());
  const uniqueModels = Array.from(new Set(filteredModels));

  // Sort models alphabetically
  uniqueModels.sort((a, b) => a.localeCompare(b));

  console.log(
    `[ModelFilter] Models for mark ${mark}: ${JSON.stringify(
      uniqueModels.slice(0, 5)
    )}... (${uniqueModels.length} total)`
  );

  // Convert array of model names to VehicleOption objects
  const modelOptions: VehicleOption[] = [{ key: "", text: "" }];

  uniqueModels.forEach((model) => {
    // Use the model name as both key and text
    modelOptions.push({
      key: model,
      text: model,
    });
  });

  console.log(`[ModelFilter] Created ${modelOptions.length - 1} model options`);

  return modelOptions;
};

// Function to find model by mark
export const findDefaultModelByMark = (mark: string): string => {
  const models = getModelsByMark(mark);
  // Return the first non-empty model if available
  return models.length > 1 && models[1] ? models[1].key : "";
};

// Create mark options for dropdown
export const getVehicleMarkOptions = (): VehicleOption[] => {
  const markOptions: VehicleOption[] = [{ key: "", text: "" }];

  const seenMarks = new Set<string>();

  // Extract all mark names from the object, cleaning up the problematic keys
  for (const mark in vehicleMarksAndModels) {
    // Extract just the mark name from potentially problematic keys
    const markName = (mark.split('"')[0] ?? "").trim();

    // Skip if empty
    if (!markName) continue;

    // Add to set to track unique marks (case-insensitive)
    const normalizedMarkName = markName.toUpperCase();
    if (!seenMarks.has(normalizedMarkName)) {
      seenMarks.add(normalizedMarkName);
      markOptions.push({
        key: markName,
        text: markName,
      });
    }
  }

  return markOptions;
};
